import { useMemo } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';

import { InputColor, showToastMessage } from 'enable-ui';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { updateUserProfile, useAuthStore, userProfileSelector } from 'modules/auth/core';
import { RoleKey } from 'modules/auth/core/enum';
import { renderDateOfBirthError } from 'modules/learners/utils';

import { getDateOfBirth, getInputStatus, hasEmptyValues, isValidEmail } from 'helpers';
import { requireEmailValidation } from 'helpers/validate';
import { useString } from 'hooks';
import { apiCall } from 'hooks/service';

import { ERROR_MESSAGE, SUCCESS_MESSAGE } from 'constants/message';

export const useProfileData = () => {
  const userProfile = useAuthStore(userProfileSelector);

  const roleKey = userProfile?.role?.key;

  const searchSelectMonth = useString('');
  const searchSelectYear = useString('');

  const { months, years } = getDateOfBirth();

  const formik = useFormik({
    initialValues: {
      firstName: userProfile.firstName ?? userProfile.name,
      lastName: userProfile.lastName ?? '',
      email: userProfile.email,
      phone: userProfile.phone,
      yearOfBirth: userProfile?.physicalProfile?.yearOfBirth,
      gender: userProfile?.physicalProfile?.gender,
      monthOfBirth: userProfile?.physicalProfile?.monthOfBirth,
    },

    validationSchema: Yup.object().shape({
      firstName: requireEmailValidation,
    }),

    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      const result: any = await apiCall({
        url: `/users/${userProfile.id}`,
        data: {
          ...values,
          name: `${values.firstName || ''} ${values.lastName || ''}`.trim(),
        },
        method: 'PATCH',
        isLoading: true,
        showError: true,
      });

      if (result?.id) {
        updateUserProfile({
          ...userProfile,
          ...result,
        });
        showToastMessage('success', 'Username changed successfully!');
      }
    },
  });

  const { values, isValid, isSubmitting, handleSubmit, handleChange, setFieldValue } = formik;

  const onChangeGender = (gender: string) => () => {
    setFieldValue('gender', gender);
  };

  const emailStatus: { helperText: string; color: InputColor } = useMemo(() => {
    if (values.email === userProfile.email) {
      return {
        helperText: '',
        color: 'default',
      };
    }

    return getInputStatus(
      values?.email || '',
      SUCCESS_MESSAGE.EMAIL,
      isValidEmail(String(values.email).trim()).isValid
        ? ''
        : isValidEmail(String(values.email).trim()).message,
    );
  }, [values.email, userProfile.email]);

  const disabled =
    hasEmptyValues({
      firstName: values.firstName,
      email: values.email,
      gender: values.gender,
      ...(roleKey === RoleKey.BIOLOGIC_ADMIN || roleKey === RoleKey.INSTITUTION_ADMIN
        ? {}
        : { yearOfBirth: values.yearOfBirth, phone: values.phone }),
    }) ||
    (values.phone ? !isValidPhoneNumber(values.phone) : false) ||
    emailStatus.helperText === ERROR_MESSAGE.EMAIL ||
    isSubmitting ||
    !isValid ||
    (roleKey === RoleKey.TRAINER_INSTITUTION ? Number(values.yearOfBirth) === -1 : false) ||
    (values.email ? (isValidEmail(String(values?.email).trim()).isValid ? false : true) : false) ||
    (values.firstName === (userProfile.firstName || userProfile.name) &&
      values.lastName === (userProfile.lastName || '') &&
      values.email === userProfile.email &&
      values.phone === userProfile.phone &&
      values.gender === userProfile.physicalProfile?.gender &&
      (roleKey !== RoleKey.BIOLOGIC_ADMIN && roleKey !== RoleKey.INSTITUTION_ADMIN
        ? values.monthOfBirth === userProfile.physicalProfile?.monthOfBirth &&
          values.yearOfBirth === userProfile.physicalProfile?.yearOfBirth
        : true));

  const handleChangePhone = (value: string) => {
    setFieldValue('phone', value || '');
  };

  const menuMonths = months.map((m, index) => ({
    value: String(index),
    label: String(m),
  }));

  const menuYears = years.map((y) => {
    return {
      value: String(y),
      label: y === -1 ? 'Not select' : String(y),
    };
  });

  const onChangeMonths = (item: any) => {
    setFieldValue('monthOfBirth', Number(item.value));
  };

  const onChangeYears = (item: any) => {
    setFieldValue('yearOfBirth', Number(item.value));
  };

  const onChangeSearchMonths = (value: string) => {
    searchSelectMonth.setValue(value);
  };

  const onChangeSearchYears = (value: string) => {
    searchSelectYear.setValue(value);
  };

  const yearErrorMessage = renderDateOfBirthError(
    'yearOfBirth',
    null,
    Number(values.monthOfBirth),
    Number(values.yearOfBirth),
  );

  return {
    ...values,
    disabled,
    handleSubmit,
    handleChange,
    onChangeGender,
    handleChangePhone,
    onChangeMonths,
    onChangeYears,
    onChangeSearchMonths,
    onChangeSearchYears,
    searchSelectMonth,
    searchSelectYear,
    menuYears,
    menuMonths,
    emailStatus,
    yearErrorMessage,
    roleKey,
  };
};
